import React, { Component, useEffect, useState, useRef } from 'react';
import { Embeds } from '@checkr/web-sdk';
import ReactDOM from 'react-dom';
import { toast } from 'sonner';
import {
  checkrApi,
  employeeApi,
  onboardingApi,
  extractErrorMessage,
  getAuthToken,
} from '../../../services/hrApi';
import './StepStyles.scss';

// Error boundary to catch Checkr embed errors (e.g. "container element removed from DOM")
// caused when the embed unmounts during step navigation. The Checkr SDK uses findDOMNode
// and can throw when its container is removed.
class CheckrEmbedErrorBoundary extends Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (error?.message?.includes('container element removed') || error?.message?.includes('findDOMNode')) {
      // Expected when navigating away; suppress noisy logging
      return;
    }
    console.error('[Checkr embed error]:', error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="step-info-box" style={{ borderColor: '#ffc107' }}>
          <i className="bx bx-error-circle" style={{ color: '#ffc107' }}></i>
          <p style={{ margin: 0 }}>The form was interrupted. Return to this step to try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

interface BackgroundSearchStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

const BackgroundSearchStep: React.FC<BackgroundSearchStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [stepCompleted, setStepCompleted] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [userChoseContinue, setUserChoseContinue] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [checkrConnected, setCheckrConnected] = useState(false);
  const [checkrLoading, setCheckrLoading] = useState(true);
  const [embedConfig, setEmbedConfig] = useState<{
    oauth_callback_url: string;
    partner_id: string;
    partner_name: string;
    env: string;
  } | null>(null);
  const [inviteConfig, setInviteConfig] = useState<{
    session_token_path: string;
    env: string;
  } | null>(null);
  const [employee, setEmployee] = useState<any>(null);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [invitationStatusLoading, setInvitationStatusLoading] = useState(false);
  const [reloadingStatus, setReloadingStatus] = useState(false);
  const [reportsEmbedKey, setReportsEmbedKey] = useState(0);
  const [disconnecting, setDisconnecting] = useState(false);
  const [checkrUser, setCheckrUser] = useState<{ account?: { name?: string }; users?: Array<{ full_name?: string; email?: string }> } | null>(null);
  const [skipSaving, setSkipSaving] = useState(false);
  const embedContainerRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const SignUpFlowComponent = Embeds.SignUpFlow.useReact(React, ReactDOM);
  const NewInvitationComponent = Embeds.NewInvitation.useReact(React, ReactDOM);
  const ReportsOverviewComponent = Embeds.ReportsOverview.useReact(React, ReactDOM);

  const checkStatus = async () => {
    try {
      const status = await checkrApi.checkStatus();
      setCheckrConnected(status.connected || false);
      return status.connected;
    } catch (error) {
      console.error('Error checking Checkr status:', error);
      setCheckrConnected(false);
      return false;
    } finally {
      setCheckrLoading(false);
    }
  };

  const loadOauthInit = async () => {
    setEmbedError(null);
    try {
      const res = await checkrApi.oauthInit();
      if (res.success && res.oauth_callback_url) {
        setEmbedConfig({
          oauth_callback_url: res.oauth_callback_url,
          partner_id: res.partner_id || res.partner_name || 'BizServe',
          partner_name: res.partner_name || 'BizServe',
          env: res.env || 'staging',
        });
      } else {
        setEmbedError('Checkr is not configured. Please contact support.');
      }
    } catch (error) {
      setEmbedError(extractErrorMessage(error));
    }
  };

  const loadInviteConfig = async () => {
    try {
      const res = await checkrApi.getInviteConfig();
      if (res.success && res.session_token_path) {
        setInviteConfig({
          session_token_path: res.session_token_path,
          env: res.env || 'staging',
        });
      }
    } catch (error) {
      console.error('Error loading invite config:', error);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  // Checkr SDK can throw "Detected container element removed from DOM" when user navigates away
  // or cross-origin "Script error." — only suppress while this step is mounted so other cross-origin errors elsewhere still show.
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const msg = String(event?.message || '').trim();
      if (
        msg.includes('container element removed from DOM') ||
        msg.includes('Target container is not a DOM element') ||
        msg === 'Script error.'
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return true;
      }
      return false;
    };
    window.addEventListener('error', handleError, true);
    return () => window.removeEventListener('error', handleError, true);
  }, []);

  // Load step completion and invitation status from backend (determines formal question vs embed vs status view)
  useEffect(() => {
    if (!employeeId) {
      setInitLoading(false);
      setStepCompleted(false);
      setInviteSent(false);
      return;
    }
    setInitLoading(true);
    Promise.all([
      onboardingApi.getOnboardingProgressByEmployee(employeeId),
      checkrApi.getInvitationStatus(employeeId),
    ])
      .then(([progressList, inviteRes]) => {
        const progress = Array.isArray(progressList) ? progressList : [];
        const bgProgress = progress.find((p: { step_name: string }) => p.step_name === 'background_search');
        const completed = !!(bgProgress && bgProgress.completed);
        setStepCompleted(completed);
        setInviteSent(!!(inviteRes && (inviteRes as { sent?: boolean }).sent));
        if (completed) onComplete(true);
      })
      .catch(() => {
        setStepCompleted(false);
        setInviteSent(false);
      })
      .finally(() => setInitLoading(false));
  }, [employeeId]);

  useEffect(() => {
    if (checkrConnected && employeeId) {
      employeeApi
        .getEmployee(employeeId)
        .then(setEmployee)
        .catch(() => setEmployee(null));
      loadInviteConfig();
    }
  }, [checkrConnected, employeeId]);

  // When invite was sent but step not yet complete, poll refresh-status so we can mark step done when report completes
  useEffect(() => {
    if (!employeeId || !inviteSent || stepCompleted) return;
    setInvitationStatusLoading(true);
    checkrApi
      .refreshStatus(employeeId)
      .then((res: { step_completed?: boolean }) => {
        if (res?.step_completed) {
          setStepCompleted(true);
          onComplete(true);
        }
      })
      .catch(() => {})
      .finally(() => setInvitationStatusLoading(false));
  }, [employeeId, inviteSent, stepCompleted, onComplete]);

  useEffect(() => {
    if (checkrConnected) {
      checkrApi
        .getUser()
        .then((res: any) => setCheckrUser(res))
        .catch(() => setCheckrUser(null));
    } else {
      setCheckrUser(null);
    }
  }, [checkrConnected]);

  useEffect(() => {
    if (!checkrConnected && !checkrLoading && embedConfig) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await checkrApi.checkStatus();
          if (status?.connected) {
            setCheckrConnected(true);
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
        } catch {
          // ignore poll errors
        }
      }, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [checkrConnected, checkrLoading, embedConfig]);

  const handleConnectCheckr = () => {
    loadOauthInit();
  };

  const handleDisconnectCheckr = async () => {
    setDisconnecting(true);
    try {
      await checkrApi.disconnect();
      setCheckrConnected(false);
      setInviteConfig(null);
      setInviteSent(false);
      toast.success('Checkr disconnected');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSkip = async () => {
    if (!employeeId || skipSaving) return;
    setSkipSaving(true);
    try {
      await onboardingApi.saveOnboardingProgress({
        employee_id: employeeId,
        step_name: 'background_search',
        completed: true,
      });
      setStepCompleted(true);
      onComplete(true);
      toast.success('Background check step skipped');
    } catch (error) {
      toast.error(extractErrorMessage(error));
      setSkipSaving(false);
    }
  };

  const handleContinue = () => {
    setUserChoseContinue(true);
    if (!checkrConnected && !embedConfig) {
      loadOauthInit();
    }
  };

  const handleInvitationSuccess = async (response: any) => {
    if (!employeeId) return;
    try {
      const invitation = response?.invitation;
      await checkrApi.saveInvitation({
        employee_id: employeeId,
        candidate_id: response?.candidate_id || '',
        report_id: response?.report?.id || undefined,
        invitation_id: invitation?.id || undefined,
      });
      setInviteSent(true);
      toast.success('Background check invitation sent successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleInvitationError = (response: any) => {
    console.error('[Checkr NewInvitation] onInvitationError:', response);
    const errMsg =
      response?.errors?.invitation?.[0] ||
      response?.errors?.base?.[0] ||
      (typeof response === 'string' ? response : JSON.stringify(response));
    toast.error(errMsg || 'Failed to send invitation');
  };

  const handleReloadReportStatus = async () => {
    if (!employeeId || reloadingStatus) return;
    setReloadingStatus(true);
    try {
      const res = await checkrApi.refreshStatus(employeeId);
      if (res?.step_completed) {
        setStepCompleted(true);
        onComplete(true);
      }
      // Force remount of ReportsOverview so latest backend data is reflected.
      setReportsEmbedKey((prev) => prev + 1);
      toast.success('Background check status refreshed');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setReloadingStatus(false);
    }
  };

  const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : 'this employee';

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Background Search & Verification</h4>
        <p>Conduct background verification for the employee</p>
        {userChoseContinue && (
          <div className="docusign-status-header" style={{ marginTop: 8 }}>
            {checkrLoading ? (
              <span className="docusign-status-loading">
                <i className="bx bx-loader-alt bx-spin"></i>
                Checking Checkr...
              </span>
            ) : checkrConnected ? (
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span className="docusign-status-connected" style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                  <i className="bx bx-check-circle"></i>
                  Checkr: Connected
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleDisconnectCheckr}
                    disabled={disconnecting}
                    style={{ marginLeft: 4 }}
                  >
                    {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </span>
                {checkrUser?.account?.name && (
                  <small style={{ color: '#6c757d', marginLeft: 24 }}>
                    Account: {checkrUser.account.name}
                  </small>
                )}
              </span>
            ) : (
              <span className="docusign-status-disconnected">
                <i className="bx bx-x-circle"></i>
                Checkr: Not connected
              </span>
            )}
          </div>
        )}
      </div>

      {!employeeId ? (
        <div className="step-form">
          <div className="step-info-box">
            <i className="bx bx-info-circle" style={{ fontSize: 24 }}></i>
            <div>
              <strong>Select an employee</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                Navigate to an employee in the onboarding wizard to run a background check or skip this step.
              </p>
            </div>
          </div>
        </div>
      ) : initLoading ? (
        <div className="step-form">
          <div className="step-info-box">
            <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 24 }}></i>
            <p style={{ margin: 0 }}>Loading...</p>
          </div>
        </div>
      ) : stepCompleted ? (
        <div className="step-form">
          <div className="step-info-box" style={{ borderColor: '#198754' }}>
            <i className="bx bx-check-circle" style={{ color: '#198754', fontSize: 24 }}></i>
            <div>
              <strong>{inviteSent ? 'Background check completed' : 'Background check skipped'}</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                {inviteSent
                  ? `Background verification for ${employeeName} is in progress or complete. Check the status embed below if you need details.`
                  : `You chose to skip the background check for ${employeeName}.`}
              </p>
            </div>
          </div>
          {inviteSent && inviteConfig && (
            <>
              <div style={{ marginTop: 16, marginBottom: 12 }}>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleReloadReportStatus}
                  disabled={reloadingStatus}
                >
                  <i className={`bx ${reloadingStatus ? 'bx-loader-alt bx-spin' : 'bx-refresh'}`} style={{ marginRight: 6 }}></i>
                  {reloadingStatus ? 'Reloading...' : 'Reload'}
                </button>
              </div>
              <div ref={embedContainerRef} id="checkr-reports-overview-container" style={{ minHeight: 300, marginTop: 16 }}>
                <CheckrEmbedErrorBoundary>
                  <ReportsOverviewComponent
                    key={reportsEmbedKey}
                    sessionTokenPath={inviteConfig.session_token_path}
                    sessionTokenRequestHeaders={() => ({
                      Authorization: `Bearer ${getAuthToken() || ''}`,
                    })}
                    externalCandidateId={employeeId}
                    env={inviteConfig.env}
                  />
                </CheckrEmbedErrorBoundary>
              </div>
            </>
          )}
        </div>
      ) : !inviteSent && !userChoseContinue ? (
        <div className="step-form">
          <div className="step-info-box" style={{ marginBottom: 16 }}>
            <i className="bx bx-info-circle" style={{ fontSize: 24 }}></i>
            <div>
              <strong>Run a background check?</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                We use Checkr to run background checks. You can continue to send an invitation to{' '}
                <strong>{employeeName}</strong> or skip this step.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleContinue}
              disabled={skipSaving}
            >
              Continue
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSkip}
              disabled={skipSaving}
            >
              {skipSaving ? 'Saving...' : 'Skip'}
            </button>
          </div>
        </div>
      ) : inviteSent ? (
        <div className="step-form">
          {invitationStatusLoading ? (
            <div className="step-info-box">
              <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 24 }}></i>
              <p style={{ margin: 0 }}>Checking report status...</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: 16 }}>
                Background check status for <strong>{employeeName}</strong>. The Checkr embed below shows invitation and report progress.
              </p>
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleReloadReportStatus}
                  disabled={reloadingStatus}
                >
                  <i className={`bx ${reloadingStatus ? 'bx-loader-alt bx-spin' : 'bx-refresh'}`} style={{ marginRight: 6 }}></i>
                  {reloadingStatus ? 'Reloading...' : 'Reload'}
                </button>
              </div>
              {inviteConfig && (
                <div ref={embedContainerRef} id="checkr-reports-overview-container" style={{ minHeight: 400 }}>
                  <CheckrEmbedErrorBoundary>
                    <ReportsOverviewComponent
                      key={reportsEmbedKey}
                      sessionTokenPath={inviteConfig.session_token_path}
                      sessionTokenRequestHeaders={() => ({
                        Authorization: `Bearer ${getAuthToken() || ''}`,
                      })}
                      externalCandidateId={employeeId}
                      env={inviteConfig.env}
                    />
                  </CheckrEmbedErrorBoundary>
                </div>
              )}
            </>
          )}
        </div>
      ) : userChoseContinue && (checkrLoading || !checkrConnected) && !embedConfig ? (
        <div className="coming-soon-container">
          <div className="coming-soon-icon">
            <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 48 }}></i>
          </div>
          <p>Checking Checkr connection...</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConnectCheckr}
            disabled={checkrLoading}
            style={{ marginTop: 16 }}
          >
            <i className="bx bx-link-alt" style={{ marginRight: 8 }}></i>
            Connect Checkr
          </button>
        </div>
      ) : userChoseContinue && !checkrConnected && embedConfig ? (
        <div className="step-form">
          {embedError && (
            <div className="step-info-box" style={{ borderColor: '#dc3545', marginBottom: 16 }}>
              <i className="bx bx-error-circle" style={{ color: '#dc3545' }}></i>
              <p>{embedError}</p>
            </div>
          )}
          <div ref={embedContainerRef} id="checkr-signup-embed-container" style={{ minHeight: 400 }}>
            <CheckrEmbedErrorBoundary>
              <SignUpFlowComponent
                oauthTokenPath={embedConfig.oauth_callback_url}
                partner={{ id: embedConfig.partner_id, name: embedConfig.partner_name }}
                env={embedConfig.env}
              />
            </CheckrEmbedErrorBoundary>
          </div>
        </div>
      ) : userChoseContinue && checkrConnected && inviteConfig && employeeId ? (
        <div className="step-form">
          <p style={{ marginBottom: 16 }}>
            Send a background check invitation to <strong>{employeeName}</strong>. The employee will receive an email from Checkr to complete the process.
          </p>
          <div ref={embedContainerRef} id="checkr-invite-embed-container" style={{ minHeight: 400 }}>
            <CheckrEmbedErrorBoundary>
              <NewInvitationComponent
                sessionTokenPath={inviteConfig.session_token_path}
                sessionTokenRequestHeaders={() => ({
                  Authorization: `Bearer ${getAuthToken() || ''}`,
                })}
                defaultEmail={employee?.email || ''}
                externalCandidateId={employeeId}
                env={inviteConfig.env}
                onInvitationSuccess={handleInvitationSuccess}
                onInvitationError={handleInvitationError}
              />
            </CheckrEmbedErrorBoundary>
          </div>
        </div>
      ) : userChoseContinue ? (
        <div className="step-form">
          <div className="step-info-box">
            <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 24 }}></i>
            <p style={{ margin: 0 }}>Loading Checkr...</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BackgroundSearchStep;
